import React, { useEffect, useMemo, Fragment } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { imgUrl } from 'src/actions/types';
import { Link } from 'react-router-dom'; 
// --- Import all necessary Redux actions ---
import { getAllDoctors } from 'src/actions/doctorActions';
import { getAllPatientProfiles } from 'src/actions/patientProfileActions';
import { getAllAppointments } from 'src/actions/appointmentActions';
import { listAllInvoices } from 'src/actions/invoiceActions'; // <-- Keep this for revenue
import { formatCurrency } from '../utils/currencyHelper';
// --- Helper Functions ---
// const formatCurrency = (amount) => {
//     return new Intl.NumberFormat('en-IN', {
//         style: 'currency',
//         currency: 'INR',
//         maximumFractionDigits: 0,
//     }).format(amount);
// };

// --- Main Admin Dashboard Component ---
const AdminDashboardMain = ({
    doctors,
    patients,
    appointments,
    invoices, // <-- Keep invoices for revenue
    isLoading,
    getAllDoctors,
    getAllPatientProfiles,
      selectedCurrency, 
    getAllAppointments,
    listAllInvoices, // <-- Keep this action
}) => {
    // --- Fetch all data on component mount ---
    useEffect(() => {
        getAllDoctors();
        getAllPatientProfiles();
        getAllAppointments();
        listAllInvoices(); // Fetch both appointments and invoices
    }, [getAllDoctors, getAllPatientProfiles, getAllAppointments, listAllInvoices]);

    // --- Memoized hook to calculate dashboard statistics ---
    const dashboardStats = useMemo(() => {
        const doctorCount = doctors.length;
        const patientCount = patients.length;
        // The appointment count should come from the appointments array
        const appointmentCount = appointments.length;

        // Revenue calculation correctly uses INVOICES and excludes 'Pending'
        const totalRevenue = invoices.reduce((sum, invoice) => {
            if (invoice.Status && invoice.Status.toLowerCase() == 'paid') {
                return sum + (parseFloat(invoice.TotalAmount) || 0);
            }
            return sum;
        }, 0);

        return {
            doctorCount,
            patientCount,
            appointmentCount,
            totalRevenue,
        };
    }, [doctors, patients, appointments, invoices]); // <-- Use both in dependency array

    // --- Memoized hooks for recent lists ---
    const recentDoctors = useMemo(() => doctors.slice(0, 5), [doctors]);
    const recentPatients = useMemo(() => patients.slice(0, 5), [patients]);
    
    // This now correctly uses the APPOINTMENTS array again
    const recentAppointments = useMemo(() => {
        if (patients.length === 0 || doctors.length === 0 || appointments.length === 0) return [];
        
        const patientMap = new Map(patients.map(p => [p.PatientID, p]));
        const doctorMap = new Map(doctors.map(d => [d.hospitalID, d]));
        
        return appointments
            .map(apt => ({
                ...apt,
                patient: patientMap.get(apt.PatientID) || {},
                doctor: doctorMap.get(apt.hospitalID) || {},
            }))
            .sort((a,b) => new Date(b.appointmentDate) - new Date(a.appointmentDate))
            .slice(0, 5);
    }, [appointments, patients, doctors]);


    return (
        <Fragment>
            <Helmet>
                <title>Admin Dashboard | Medisevas</title>
            </Helmet>
            
            <div className="page-wrapper ml-0  mt-0 ">
                <div className="content-container">
                    {/* <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12 col-lg-12">
                                <h3 className="page-title">Welcome Admin!</h3>
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item active">Dashboard</li>
                                </ul>
                            </div>
                        </div>
                    </div> */}
                    <div className="page-header filter-bar rounded">
                        <div className="row align-items-center">
                            <div className="col">
                                <h4 className="page-title"> Admin Dashboard </h4>
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/doctor/dashboard">Admin Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item active">Admin Dashboard</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* --- Stat Cards --- */}
                    <div className="row">
                        <div className="col-xl-3 col-sm-6 col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="dash-widget-header">
                                        <span className="dash-widget-icon text-primary border-primary"><i className="fe fe-users" /></span>
                                        <div className="dash-count"><h3>{isLoading ? '...' : dashboardStats.doctorCount}</h3></div>
                                    </div>
                                    <div className="dash-widget-info"><h6 className="text-muted">Doctors</h6><div className="progress progress-sm"><div className="progress-bar bg-primary w-50" /></div></div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-sm-6 col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="dash-widget-header">
                                        <span className="dash-widget-icon text-success"><i className="fe fe-credit-card" /></span>
                                        <div className="dash-count"><h3>{isLoading ? '...' : dashboardStats.patientCount}</h3></div>
                                    </div>
                                    <div className="dash-widget-info"><h6 className="text-muted">Patients</h6><div className="progress progress-sm"><div className="progress-bar bg-success w-50" /></div></div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-sm-6 col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="dash-widget-header">
                                        <span className="dash-widget-icon text-danger border-danger"><i className="fe fe-money" /></span>
                                        <div className="dash-count"><h3>{isLoading ? '...' : dashboardStats.appointmentCount}</h3></div>
                                    </div>
                                    <div className="dash-widget-info"><h6 className="text-muted">Appointments</h6><div className="progress progress-sm"><div className="progress-bar bg-danger w-50" /></div></div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-sm-6 col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="dash-widget-header">
                                        <span className="dash-widget-icon text-warning border-warning"><i className="fe fe-folder" /></span>
                                        <div className="dash-count"><h3>{isLoading ? '...' : formatCurrency(dashboardStats.totalRevenue, selectedCurrency)}</h3></div>
                                    </div>
                                    <div className="dash-widget-info"><h6 className="text-muted">Revenue</h6><div className="progress progress-sm"><div className="progress-bar bg-warning w-50" /></div></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* --- Recent Doctors and Patients Tables --- */}
                    <div className="row">
                        <div className="col-md-6 d-flex">
                            <div className="card card-table flex-fill">
                                <div className="card-header"><h4 className="card-title">Recent Doctors</h4></div>
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-hover table-center mb-0">
                                            <thead><tr><th>Doctor Name</th><th>Speciality</th></tr></thead>
                                            <tbody>
                                                {recentDoctors.map(doctor => (
                                                    <tr key={doctor.hospitalID}>
                                                        <td>
                                                            <h2 className="table-avatar">
                                                                <span className="avatar avatar-sm mr-2">
                                                                    <img className="avatar-img rounded-circle" src={doctor.uploadPhoto ? `${imgUrl}${doctor.uploadPhoto}` : '/assets/img/doctors/doctor-thumb-01.jpg'} alt="Doctor" />
                                                                </span>
                                                                <strong>Dr. {doctor.firstName} {doctor.lastName}</strong>
                                                            </h2>
                                                        </td>
                                                        <td>{doctor.Specializations?.[0]?.Specialization || 'N/A'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 d-flex">
                            <div className="card card-table flex-fill">
                                <div className="card-header"><h4 className="card-title">Recent Patients</h4></div>
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-hover table-center mb-0">
                                            <thead><tr><th>Patient Name</th><th>Phone</th><th>Last Visit</th></tr></thead>
                                            <tbody>
                                                {recentPatients.map(patient => (
                                                    <tr key={patient.PatientID}>
                                                        <td>
                                                            <h2 className="table-avatar">
                                                                <span className="avatar avatar-sm mr-2">
                                                                    <img className="avatar-img rounded-circle" src={patient.uploadPhoto ? `${imgUrl}${patient.uploadPhoto}` : '/assets/img/patients/patient.jpg'} alt="Patient" />
                                                                </span>
                                                                <strong>{patient.firstName} {patient.lastName}</strong>
                                                            </h2>
                                                        </td>
                                                        <td>{patient.phoneNumber || 'N/A'}</td>
                                                        <td>{new Date().toLocaleDateString()}</td>{/* Placeholder */}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* --- Recent Appointments Table --- */}
                    <div className="row">
                        <div className="col-md-12">
                            <div className="card card-table">
                                <div className="card-header"><h4 className="card-title">Recent Appointments</h4></div>
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-hover table-center mb-0">
                                            <thead><tr><th>Doctor Name</th><th>Patient Name</th><th>Appointment Time</th><th className="text-right">Amount</th></tr></thead>
                                            <tbody>
                                                {recentAppointments.map(apt => (
                                                    <tr key={apt.appointmentId}>
                                                        <td>
                                                            <h2 className="table-avatar">
                                                                <span className="avatar avatar-sm mr-2">
                                                                    <img className="avatar-img rounded-circle" src={apt.doctor.uploadPhoto ? `${imgUrl}${apt.doctor.uploadPhoto}` : '/assets/img/doctors/doctor-thumb-01.jpg'} alt="Doctor" />
                                                                </span>
                                                                <strong>Dr. {apt.doctor.firstName} {apt.doctor.lastName}</strong>
                                                            </h2>
                                                        </td>
                                                        <td>
                                                            <h2 className="table-avatar">
                                                                <span className="avatar avatar-sm mr-2">
                                                                    <img className="avatar-img rounded-circle" src={apt.patient.uploadPhoto ? `${imgUrl}${apt.patient.uploadPhoto}` : '/assets/img/patients/patient.jpg'} alt="Patient" />
                                                                </span>
                                                                <strong>{apt.patient.firstName} {apt.patient.lastName}</strong>
                                                            </h2>
                                                        </td>
                                                        <td>{new Date(apt.appointmentDate).toLocaleDateString()} <span className="text-primary d-block">{apt.appointmentTime}</span></td>
                                                       <td className="text-right">{formatCurrency(apt.paidAmount || 0, selectedCurrency)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = (state) => ({
    doctors: state.doctorList.doctors || [],
    patients: state.patientProfile.profiles || [],
    appointments: state.appointment.all || [],
    invoices: state.invoice.invoices || [], // <-- Get invoices for revenue
    selectedCurrency: state.currencyReducer.selectedCurrency, 
    isLoading: state.doctorList.loading || state.patientProfile.loading || state.appointment.loading || state.invoice.loading,
});

const mapDispatchToProps = {
    getAllDoctors,
    getAllPatientProfiles,
    getAllAppointments,
    listAllInvoices, // <-- Fetch invoices
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminDashboardMain);